import { useEffect } from 'react';
import { useAccumulatorStore } from '../../stores/useAccumulatorStore';
import { getSessionUser } from '../../utils/session';
import { AccumulatorDisclaimer } from './components/AccumulatorDisclaimer';
import { AccumulatorTable } from './components/AccumulatorTable';

export interface AccumulatorMountProps {
  config: {
    customerServicePhone: string;
    customerServiceEmail: string;
  };
}

/**
 * Deductible & Out of Pocket page (route /accumulator).
 *
 * Migrated from accumulatorController.js + accumulator.html (DAED-271 Phase A),
 * then extended for SDC plans (Phase B): SDC members see year-to-date amounts
 * with dashed maximums and a guidance icon on the two Maximum headers. The
 * SDC/standard split lives in the store; this component just passes the flag down.
 */
export function AccumulatorPage({ config }: AccumulatorMountProps) {
  const { values, isSdc, loading, error, fetchAccumulator } = useAccumulatorStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional run-once on mount
  useEffect(() => {
    const sessionUser = getSessionUser();
    if (sessionUser) {
      void fetchAccumulator(sessionUser);
    }
  }, []);

  const sessionUser = getSessionUser();
  const memberName = `${sessionUser?.FirstName ?? ''} ${sessionUser?.LastName ?? ''}`.trim();

  return (
    <div className="col-lg-12 col-md-10 col-sm-12 col-xs-12">
      <div className="col-lg-12 col-md-12 col-sm-12">
        <h1>Deductible &amp; Out of Pocket</h1>
      </div>
      <hr />

      {/*
        The explicit {' '} separators below are load-bearing: .spinner > div is
        inline-block with no margin, so the gaps between the bars come from the
        whitespace text nodes the legacy HTML had between each element. JSX
        strips newline-only whitespace, which closes the bars into a solid
        block. Same fix as AutoRefillPage.
      */}
      {loading && (
        <div className="spinner large">
          <div className="rect1" />
          {' '}
          <div className="rect2" />
          {' '}
          <div className="rect3" />
          {' '}
          <div className="rect4" />
          {' '}
          <div className="rect5" />
          <h4>Loading...</h4>
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger" role="alert">
          {error} Please try again, or contact us at {config.customerServicePhone} for assistance.
        </div>
      )}

      {!loading && !error && values && (
        <AccumulatorTable memberName={memberName} values={values} isSdc={isSdc} />
      )}

      <AccumulatorDisclaimer />
    </div>
  );
}
